import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { trackEvent } from '../services/analytics';

interface PricingSectionProps {
  showTitle?: boolean;
  compact?: boolean;
}

const PricingSection: React.FC<PricingSectionProps> = ({ showTitle = true, compact = false }) => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [isYearly, setIsYearly] = useState(false);

  const handleGetStarted = (plan: string, includeStarlink: boolean = false) => {
    const billing = isYearly ? 'yearly' : 'monthly';
    const finalPlan = includeStarlink ? 'bundle' : plan;
    
    trackEvent('pricing_plan_selected', {
      plan: finalPlan,
      billing,
      location: 'landing_page'
    });

    if (isAuthenticated) {
      // Go directly to checkout if authenticated
      navigate(`/checkout?plan=${finalPlan}&billing=${billing}`);
    } else {
      // Store selection and go to auth
      localStorage.setItem('pendingCheckout', JSON.stringify({ plan: finalPlan, billing }));
      navigate(`/auth?returnTo=/checkout&action=checkout`);
    }
  };

  const baseMonthlyPrice = 4.99;
  const baseYearlyPrice = 39.99; // 10 months price
  const starlinkMonthlyPrice = 1.99;
  const starlinkYearlyPrice = 19.99; // 10 months price

  return (
    <section className="pricing-section">
      {showTitle && (
        <div className="pricing-header">
          <h2>Choose Your Mission</h2>
          <p>Start tracking satellites and launches today</p>
        </div>
      )}

      <div className="billing-toggle">
        <span className={`toggle-option ${!isYearly ? 'active' : ''}`}>
          Monthly
        </span>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={isYearly}
            onChange={(e) => setIsYearly(e.target.checked)}
          />
          <span className="slider"></span>
        </label>
        <span className={`toggle-option ${isYearly ? 'active' : ''}`}>
          Yearly
          <span className="save-badge">Save 33%</span>
        </span>
      </div>

      <div className={`pricing-cards ${compact ? 'compact' : ''}`}>
        {/* Basic Plan */}
        <div className="pricing-card">
          <div className="plan-header">
            <h3>SpacePings Pro</h3>
            <div className="price">
              <span className="amount">
                ${isYearly ? baseYearlyPrice : baseMonthlyPrice}
              </span>
              <span className="period">/{isYearly ? 'year' : 'month'}</span>
            </div>
            <div className="trial-info">
              {isYearly ? '2 months free' : '1st month free'}
            </div>
          </div>
          
          <ul className="features">
            <li>🛰️ Real-time satellite tracking</li>
            <li>🚀 Launch notifications</li>
            <li>📧 Email & push alerts</li>
            <li>🌤️ Weather integration</li>
            <li>📱 Mobile optimized</li>
          </ul>
          
          <button 
            className="get-started-btn primary"
            onClick={() => handleGetStarted('base')}
          >
            Get Started Free
          </button>
        </div>

        {/* Bundle Plan */}
        <div className="pricing-card featured">
          <div className="popular-badge">Most Popular</div>
          <div className="plan-header">
            <h3>Pro + Starlink Bundle</h3>
            <div className="price">
              <span className="amount">
                ${isYearly ? Math.round((baseYearlyPrice + starlinkYearlyPrice) * 100) / 100 : (baseMonthlyPrice + starlinkMonthlyPrice)}
              </span>
              <span className="period">/{isYearly ? 'year' : 'month'}</span>
            </div>
            <div className="trial-info">
              {isYearly ? '2 months free' : '1st month free'}
            </div>
          </div>
          
          <ul className="features">
            <li>✅ Everything in Pro</li>
            <li>🛰️ Personal Starlink tracking</li>
            <li>📊 Command center dashboard</li>
            <li>🎯 Advanced targeting</li>
            <li>📈 Performance analytics</li>
          </ul>
          
          <button 
            className="get-started-btn featured"
            onClick={() => handleGetStarted('base', true)}
          >
            Start Bundle Free
          </button>
        </div>

        {/* Starlink Add-on */}
        <div className="pricing-card">
          <div className="plan-header">
            <h3>Starlink Add-on</h3>
            <div className="price">
              <span className="amount">
                ${isYearly ? starlinkYearlyPrice : starlinkMonthlyPrice}
              </span>
              <span className="period">/{isYearly ? 'year' : 'month'}</span>
            </div>
            <div className="trial-info">
              Add to existing Pro plan
            </div>
          </div>
          
          <ul className="features">
            <li>🛰️ Starlink satellite tracking</li>
            <li>📊 Command center access</li>
            <li>🎯 Personal dish targeting</li>
            <li>📈 Connection analytics</li>
            <li>🔧 Performance monitoring</li>
          </ul>
          
          <button 
            className="get-started-btn secondary"
            onClick={() => handleGetStarted('starlink_addon')}
          >
            Add Starlink
          </button>
        </div>
      </div>

      <div className="pricing-footer">
        <p>✨ All plans include a free trial period</p>
        <p>🔒 Cancel anytime, no commitments</p>
      </div>

      <style>{`
        .pricing-section {
          padding: 4rem 2rem;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          text-align: center;
        }

        .pricing-header {
          margin-bottom: 3rem;
        }

        .pricing-header h2 {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #00d4ff, #4338ca);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .pricing-header p {
          font-size: 1.2rem;
          color: #cbd5e1;
        }

        .billing-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 3rem;
          background: rgba(255, 255, 255, 0.1);
          padding: 0.5rem;
          border-radius: 2rem;
          max-width: 300px;
          margin-left: auto;
          margin-right: auto;
        }

        .toggle-option {
          padding: 0.5rem 1rem;
          border-radius: 1.5rem;
          transition: all 0.3s ease;
          cursor: pointer;
          font-weight: 500;
        }

        .toggle-option.active {
          background: linear-gradient(135deg, #00d4ff, #4338ca);
          color: white;
        }

        .save-badge {
          background: #10b981;
          color: white;
          padding: 0.2rem 0.5rem;
          border-radius: 0.5rem;
          font-size: 0.75rem;
          margin-left: 0.5rem;
        }

        .toggle-switch {
          position: relative;
          width: 50px;
          height: 24px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.3);
          transition: 0.3s;
          border-radius: 24px;
        }

        .slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .slider {
          background: linear-gradient(135deg, #00d4ff, #4338ca);
        }

        input:checked + .slider:before {
          transform: translateX(26px);
        }

        .pricing-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto 3rem;
        }

        .pricing-cards.compact {
          max-width: 800px;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        }

        .pricing-card {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 1rem;
          padding: 2rem;
          position: relative;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .pricing-card:hover {
          transform: translateY(-5px);
          border-color: #00d4ff;
          box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
        }

        .pricing-card.featured {
          border-color: #00d4ff;
          background: rgba(0, 212, 255, 0.1);
          transform: scale(1.05);
        }

        .popular-badge {
          position: absolute;
          top: -15px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #00d4ff, #4338ca);
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 2rem;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .plan-header {
          margin-bottom: 2rem;
        }

        .plan-header h3 {
          font-size: 1.5rem;
          margin-bottom: 1rem;
          color: #00d4ff;
        }

        .price {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .amount {
          font-size: 3rem;
          font-weight: 700;
          color: white;
        }

        .period {
          font-size: 1.2rem;
          color: #cbd5e1;
        }

        .trial-info {
          color: #10b981;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .features {
          list-style: none;
          padding: 0;
          margin: 0 0 2rem 0;
          text-align: left;
        }

        .features li {
          padding: 0.5rem 0;
          color: #e2e8f0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .get-started-btn {
          width: 100%;
          padding: 1rem 2rem;
          border: none;
          border-radius: 0.75rem;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .get-started-btn.primary {
          background: linear-gradient(135deg, #00d4ff, #4338ca);
          color: white;
        }

        .get-started-btn.featured {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .get-started-btn.secondary {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .get-started-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
        }

        .pricing-footer {
          margin-top: 2rem;
          color: #cbd5e1;
        }

        .pricing-footer p {
          margin: 0.5rem 0;
        }

        @media (max-width: 768px) {
          .pricing-section {
            padding: 2rem 1rem;
          }
          
          .pricing-header h2 {
            font-size: 2rem;
          }
          
          .pricing-cards {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .pricing-card.featured {
            transform: none;
          }
          
          .amount {
            font-size: 2.5rem;
          }
        }
      `}</style>
    </section>
  );
};

export default PricingSection;